// Team-Modus der App (OA-5, Bausteine B1/B2). Winziger Zustand ohne Abhängigkeiten, weil der
// Routen-Wächter (NativeRouteGuard, im Einstieg) ihn liest. Die eigentliche Rechte-Prüfung lebt im
// nachgeladenen Team-Zugang (src/components/team/TeamZugang.tsx) und schreibt nur hierher.
//
// status   offen = noch unbekannt (Login/Schalter/Rechte laden), nein = keine Team-Seiten,
//          ja = pfade enthält die für diese Person freigegebenen Team-Adressen.
// modus    kunde | team. Reiner Client-Zustand (Scope §4: keine Datenänderung), bleibt über einen
//          Neustart erhalten. Abmelden oder Rechte weg setzt auf kunde zurück.
import { useSyncExternalStore } from "react";

export type TeamStatus = "offen" | "nein" | "ja";
export type TeamModus = "kunde" | "team";

export interface TeamZustand {
  status: TeamStatus;
  pfade: ReadonlySet<string>;
  /** Erste freigegebene Team-Seite nach Priorität (APP_TEAM_PFADE), null ohne Recht. */
  start: string | null;
  modus: TeamModus;
}

const MODUS_KEY = "cc_team_modus";

function gespeicherterModus(): TeamModus {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(MODUS_KEY) === "team" ? "team" : "kunde";
  } catch {
    return "kunde";
  }
}

const LEER: ReadonlySet<string> = new Set();
let zustand: TeamZustand = { status: "offen", pfade: LEER, start: null, modus: gespeicherterModus() };
const hoerer = new Set<() => void>();

function setzen(neu: TeamZustand) {
  zustand = neu;
  hoerer.forEach((h) => h());
}

export function teamZustand(): TeamZustand {
  return zustand;
}

export function teamAbonnieren(h: () => void): () => void {
  hoerer.add(h);
  return () => hoerer.delete(h);
}

export function useTeamZustand(): TeamZustand {
  return useSyncExternalStore(teamAbonnieren, teamZustand, teamZustand);
}

/** Ergebnis der Rechte-Prüfung. Ohne Recht fällt der Modus auf kunde zurück. */
export function teamRechteSetzen(status: TeamStatus, pfade: string[] = []): void {
  const set = status === "ja" ? new Set(pfade) : LEER;
  const start = status === "ja" ? pfade[0] ?? null : null;
  const modus = status === "nein" ? "kunde" : zustand.modus;
  if (status === "nein" && zustand.modus === "team") modusSpeichern("kunde");
  setzen({ status, pfade: set, start, modus });
}

function modusSpeichern(modus: TeamModus) {
  try {
    if (modus === "team") localStorage.setItem(MODUS_KEY, "team");
    else localStorage.removeItem(MODUS_KEY);
  } catch {
    // Ohne Speicher gilt der Modus nur bis zum Neustart.
  }
}

export function teamModusSetzen(modus: TeamModus): void {
  if (modus === zustand.modus) return;
  modusSpeichern(modus);
  setzen({ ...zustand, modus });
}

// Platz für den Knopf „Team-Bereich“ direkt über der Kunden-Fußleiste (KundeTabLeiste). Die Leiste
// meldet hier ihr Ankerelement, TeamZugang hängt den Knopf hinein. So steht er immer im gleichen
// Abstand über der Leiste (inkl. Home-Indikator) und wandert beim Scrollen nicht mit
// (Konstantin 15.10.2026, Hundeseite). Ohne Leiste (alte Ansicht) bleibt er frei schwebend.
let knopfAnker: HTMLElement | null = null;
const ankerHoerer = new Set<() => void>();

function ankerSetzen(el: HTMLElement | null) {
  if (el === knopfAnker) return;
  knopfAnker = el;
  ankerHoerer.forEach((h) => h());
}

/** Fußleiste ist da: Knopf hier einhängen. Liefert das Aufräumen (löst nur den eigenen Anker). */
export function teamKnopfAnkerMelden(el: HTMLElement): () => void {
  ankerSetzen(el);
  return () => { if (knopfAnker === el) ankerSetzen(null); };
}

function ankerAbonnieren(h: () => void): () => void {
  ankerHoerer.add(h);
  return () => ankerHoerer.delete(h);
}

export function useTeamKnopfAnker(): HTMLElement | null {
  return useSyncExternalStore(ankerAbonnieren, () => knopfAnker, () => null);
}
